import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { machineApi, customerApi, documentationApi } from '../api/machines';
import type { Machine, Customer, DocumentationListItem, PageResponse } from '../types';
import { Toast, useToast } from '../components/Toast';
import DownloadButton from '../components/DownloadButton';
import { documentationDownloadUrl } from '../utils/fileUrl';

const PAGE_SIZE = 12;

const Documentation: React.FC = () => {
  const { user } = useAuth();
  const isAdmin = !!user?.roles?.includes('ADMIN');
  const isStaff = isAdmin || !!user?.roles?.includes('EMPLOYEE_ADMIN') || !!user?.roles?.includes('EMPLOYEE');
  const { toast, showToast, dismissToast } = useToast();

  const [machines, setMachines] = useState<Machine[]>([]);
  const [customers, setCustomers] = useState<Customer[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedNip, setSelectedNip] = useState('');
  const [selectedMachineId, setSelectedMachineId] = useState('');
  const [docsPage, setDocsPage] = useState<PageResponse<DocumentationListItem> | null>(null);
  const [loadingDocs, setLoadingDocs] = useState(false);
  const [page, setPage] = useState(0);

  // Machines are scoped by role automatically; customers give us readable names.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const { data } = await machineApi.getAll();
        if (!cancelled) setMachines(data);
      } catch { /* ignore */ }
      try {
        const list = isStaff
          ? (await customerApi.getAll(true)).data
          : [(await customerApi.getMe()).data];
        if (!cancelled) setCustomers(list);
      } catch { if (!cancelled) setCustomers([]); }
      if (!cancelled) setLoading(false);
    })();
    return () => { cancelled = true; };
  }, [isStaff]);

  // Dokumentacja filtrowana i stronicowana po stronie backendu — każda zmiana filtra/strony
  // to jeden request z parametrami nip/machineId/page.
  const loadDocs = useCallback(async () => {
    setLoadingDocs(true);
    try {
      const { data } = await documentationApi.listAll({
        nip: selectedNip || undefined,
        machineId: selectedMachineId || undefined,
        page,
        size: PAGE_SIZE,
      });
      setDocsPage(data);
      // Backend przycina numer strony poza zakresem — synchronizujemy stan lokalny.
      if (data.page !== page) setPage(data.page);
    } catch { setDocsPage(null); }
    finally { setLoadingDocs(false); }
  }, [selectedNip, selectedMachineId, page]);

  useEffect(() => { loadDocs(); }, [loadDocs]);

  const nipToName = useMemo(() => {
    const m: Record<string, string> = {};
    customers.forEach((c) => { if (c.nip) m[c.nip] = c.companyName || ''; });
    return m;
  }, [customers]);

  const machineById = useMemo(() => {
    const m: Record<string, Machine> = {};
    machines.forEach((mm) => { m[mm.id] = mm; });
    return m;
  }, [machines]);

  // Only customers that actually have machines the user can see.
  const customerOptions = useMemo(() => {
    const nips = Array.from(new Set(machines.map((m) => m.ownerNip).filter(Boolean)));
    return nips
      .map((nip) => ({ nip, label: nipToName[nip] || nip }))
      .sort((a, b) => a.label.localeCompare(b.label, 'pl'));
  }, [machines, nipToName]);

  // Maszyny klienta należące do integratora (machineApi.getAll jest już ograniczone rolą).
  // Bez wybranego klienta lista jest pusta → dropdown maszyny nie pokazuje opcji.
  const machineOptions = useMemo(
    () => (selectedNip
      ? machines
        .filter((m) => m.ownerNip === selectedNip)
        .sort((a, b) => a.name.localeCompare(b.name, 'pl'))
      : []),
    [machines, selectedNip]
  );

  const pageDocs = docsPage?.content ?? [];
  const totalPages = docsPage?.totalPages ?? 1;
  const totalElements = docsPage?.totalElements ?? 0;
  const currentPage = docsPage?.page ?? 0;

  const unlinkDoc = async (machineId: string, docId: string) => {
    try {
      await documentationApi.unlink(machineId, docId);
      await loadDocs();
      showToast('success', 'Dokument odpięty od maszyny.');
    } catch (e: any) {
      showToast('error', e?.response?.data?.message || 'Nie udało się odpiąć dokumentu.');
    }
  };

  const deleteDoc = async (docId: string) => {
    try {
      await documentationApi.delete(docId);
      await loadDocs();
      showToast('success', 'Dokument usunięty.');
    } catch (e: any) {
      showToast('error', e?.response?.data?.message || 'Nie udało się usunąć dokumentu.');
    }
  };

  const machineLabel = (id: string) => machineById[id]?.name || 'nieznana maszyna';

  const selStyle: React.CSSProperties = { flex: 1, minWidth: 240 };

  return (
    <div>
      <div className="page-header">
        <h1 className="page-title">Dokumentacja</h1>
      </div>

      {loading ? (
        <div className="empty-state">Ładowanie…</div>
      ) : (
        <>
          <div style={{ display: 'flex', gap: '1rem', flexWrap: 'wrap', marginBottom: '1.5rem' }}>
            <div className="form-group" style={selStyle}>
              <label className="form-label">Klient</label>
              <select
                className="form-select"
                value={selectedNip}
                onChange={(e) => { setSelectedNip(e.target.value); setSelectedMachineId(''); setPage(0); }}
              >
                <option value="">Wszyscy klienci</option>
                {customerOptions.map((c) => (
                  <option key={c.nip} value={c.nip}>{c.label} (NIP {c.nip})</option>
                ))}
              </select>
            </div>
            <div className="form-group" style={selStyle}>
              <label className="form-label">Maszyna</label>
              <select
                className="form-select"
                value={selectedMachineId}
                disabled={!selectedNip}
                onChange={(e) => { setSelectedMachineId(e.target.value); setPage(0); }}
              >
                <option value="">{selectedNip ? 'Wszystkie maszyny klienta' : 'najpierw wybierz klienta'}</option>
                {machineOptions.map((m) => (
                  <option key={m.id} value={m.id}>{m.name}</option>
                ))}
              </select>
            </div>
          </div>

          {loadingDocs ? (
            <div className="empty-state">Ładowanie dokumentacji…</div>
          ) : pageDocs.length === 0 ? (
            <div className="empty-state">
              {selectedMachineId
                ? 'Brak dokumentacji dla tej maszyny.'
                : selectedNip
                  ? 'Brak dokumentacji dla tego klienta.'
                  : 'Brak dokumentacji.'}
            </div>
          ) : (
            <>
              <div className="item-meta" style={{ marginBottom: '0.75rem' }}>
                {totalElements} {totalElements === 1 ? 'dokument' : 'dokumentów'}
                {totalPages > 1 && ` · strona ${currentPage + 1} z ${totalPages}`}
              </div>
              <div className="items-list">
                {pageDocs.map((d) => (
                  <div
                    key={d.id}
                    className="item-card card"
                    style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '1rem', flexWrap: 'wrap' }}
                  >
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <p className="item-desc"><strong>{d.name}</strong></p>
                      {d.description && <p className="item-meta">{d.description}</p>}
                      {d.linkedMachineIds.length > 0 && (
                        <p className="item-meta">
                          Maszyny: {d.linkedMachineIds.map(machineLabel).join(', ')}
                        </p>
                      )}
                      <div style={{ marginTop: '0.6rem' }}>
                        <DownloadButton href={documentationDownloadUrl(d.id)} />
                      </div>
                    </div>
                    {isStaff && (
                      <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap', justifyContent: 'flex-end', flexShrink: 0 }}>
                        {/* Odpięcie od maszyny ma sens tylko dla konkretnej wybranej maszyny. */}
                        {selectedMachineId && (
                          <button className="btn btn-primary" onClick={() => unlinkDoc(selectedMachineId, d.id)}>
                            Odepnij od maszyny
                          </button>
                        )}
                        {isAdmin && (
                          <button className="btn btn-danger" onClick={() => deleteDoc(d.id)}>
                            Usuń całkowicie
                          </button>
                        )}
                      </div>
                    )}
                  </div>
                ))}
              </div>

              {totalPages > 1 && (
                <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: '1rem', marginTop: '1.5rem' }}>
                  <button
                    className="btn btn-secondary"
                    disabled={currentPage === 0}
                    onClick={() => setPage((p) => Math.max(0, p - 1))}
                  >
                    Poprzednia
                  </button>
                  <span className="item-meta">Strona {currentPage + 1} z {totalPages}</span>
                  <button
                    className="btn btn-secondary"
                    disabled={currentPage >= totalPages - 1}
                    onClick={() => setPage((p) => Math.min(totalPages - 1, p + 1))}
                  >
                    Następna
                  </button>
                </div>
              )}
            </>
          )}
        </>
      )}

      <Toast toast={toast} onDismiss={dismissToast} />
    </div>
  );
};

export default Documentation;
